import { computed, observable, ObservableMap } from 'mobx';
import { IFamilyFromServer, UserFamilyService } from '../Services/UserFamilyService';
import { IUserFact, IUserFromServer, UserService } from '../Services/UserService';
import { IPublicUser } from '../_interfaces/IUser';
import { NoProfileImageUrl } from '../images/images';
import { IInviteInformation } from './OnboardingStore';

export interface IUserFamily {
    id: number;
    name: string;
    members: Array<IUserFromFamily>;
    pendingMembers: Array<{ email: string }>;
    creator: IUserFromFamily;
}

export interface IUserFromFamily {
    id: number;
    firstName: string;
    lastName: string;
    profileImageUrl: string;
    joinedFamilyTimestamp: string;
}

export class UserFamilyStore {
    @observable
    private _userFamilies: ObservableMap<string, IUserFamily> | undefined = undefined;
    private _users: Array<IUserFromServer> = [];
    private userFamilyService: UserFamilyService;
    private userService: UserService;

    public get users(): Array<IUserFromServer> {
        return this._users;
    }

    @computed
    public get userFamilies(): Array<IUserFamily> {
        return this._userFamilies ? Array.from(this._userFamilies.values()) : [];
    }

    @computed
    public get familiesAreLoaded(): boolean {
        return !!this._userFamilies;
    }

    public getFamilyByID(id: number | undefined): IUserFamily {
        return this._userFamilies!.get((id || '').toString())!;
    }

    public async getOrLoadFamilyByID(id: number): Promise<IUserFamily> {
        const family = this.getFamilyByID(id);
        if (family) {
            return family;
        }
        const familyFromServer = await this.userFamilyService.getFamilyByID(id);

        const users = await this.userService.getVisibleUsers();

        const admin = users.find(u => u.id === familyFromServer.user_id)!;
        const pendingMembers = await this.userFamilyService.getPendingMembersForFamily(familyFromServer.id);
        const newFamily = {
            id: familyFromServer.id,
            name: familyFromServer.name,
            members: familyFromServer.users_info
                ? familyFromServer.users_info.map(userInfo => {
                      const user = users.find(u => u.id === userInfo.user_id)!;

                      return {
                          id: userInfo.user_id,
                          firstName: user.first_name,
                          lastName: user.last_name,
                          profileImageUrl: user.profile_image_url || NoProfileImageUrl,
                          joinedFamilyTimestamp: familyFromServer.users_info!.find(u => u.user_id === user.id)!.joined_at
                      };
                  })
                : [],
            creator: {
                id: admin.id,
                firstName: admin.first_name,
                lastName: admin.last_name,
                profileImageUrl: admin.profile_image_url || NoProfileImageUrl,
                joinedFamilyTimestamp: familyFromServer.users_info!.find(u => u.user_id === admin.id)!.joined_at
            },
            pendingMembers
        };

        this._userFamilies!.set(newFamily.id.toString(), newFamily);
        return newFamily;
    }

    public getFamilyMemberByID(userID: number): IUserFromServer | undefined {
        return this._users.find(user => user.id === userID)!;
    }

    public updateFamilyName(familyID: number, name: string): void {
        this.userFamilyService.renameFamily(familyID, name);
        const family = this._userFamilies!.get(familyID.toString())!;
        family.name = name;
    }

    public isFamilyAdmin(family: IUserFamily, user: IPublicUser): boolean {
        return family.creator.id === user.id;
    }

    public getFamilyMemberFullName(user: IUserFromFamily): string {
        return `${user.firstName} ${user.lastName}`;
    }

    public async createFamily(familyName: string, invites: Array<IInviteInformation>): Promise<IFamilyFromServer> {
        const membersToInvite = invites.filter(i => i.email.trim() !== '' && i.firstName.trim() !== '');
        return this.userFamilyService.createFamily(familyName).then(async response => {
            await Promise.all(
                membersToInvite.map(async member => {
                    return this.userFamilyService.inviteUserToFamily(response.id, member.email, member.firstName);
                })
            );

            await this.loadFamilies();
            return response;
        });
    }

    public removeMemberFromFamily(familyID: number, userID: number): void {
        if (this.getFamilyByID(familyID).creator.id === userID) {
            alert('Family admin cannot be removed');
            return;
        }
        this.userFamilyService.removeUserFromFamily(familyID, userID);
        const family = this._userFamilies!.get(familyID.toString())!;
        family.members = family.members.filter(member => member.id !== userID);
    }

    public inviteUserToFamily(familyID: number, email: string, first_name: string): void {
        this.userFamilyService.inviteUserToFamily(familyID, email, first_name);
        const foundFamily = this._userFamilies!.get(familyID.toString())!;
        foundFamily.pendingMembers.push({ email });
    }

    public getUserFact(userID: number, factID: string): IUserFact | undefined {
        const user = this.getFamilyMemberByID(userID);
        return user && user.facts ? user.facts.find(fact => fact.user_fact_type_id === factID) : undefined;
    }

    constructor(token: string) {
        this.userFamilyService = new UserFamilyService(token);
        this.userService = new UserService(token);

        this.loadFamilies();
    }

    public loadFamilies(): Promise<void> {
        return this.userService.getVisibleUsers().then(users => {
            this._users = users;

            return this.userFamilyService.getAllFamilies().then(async response => {
                const newFamilies = await Promise.all(
                    response.map(async familyFromServer => {
                        const admin = users.find(u => u.id === familyFromServer.user_id)!;
                        const pendingMembers = await this.userFamilyService.getPendingMembersForFamily(familyFromServer.id);
                        return {
                            id: familyFromServer.id,
                            name: familyFromServer.name,
                            members: familyFromServer.users_info
                                ? familyFromServer.users_info.map(userInfo => {
                                      const user = users.find(u => u.id === userInfo.user_id)!;

                                      return {
                                          id: userInfo.user_id,
                                          firstName: user.first_name,
                                          lastName: user.last_name,
                                          profileImageUrl: user.profile_image_url || NoProfileImageUrl,
                                          joinedFamilyTimestamp: familyFromServer.users_info!.find(u => u.user_id === user.id)!.joined_at
                                      };
                                  })
                                : [],
                            creator: {
                                id: admin.id,
                                firstName: admin.first_name,
                                lastName: admin.last_name,
                                profileImageUrl: admin.profile_image_url || NoProfileImageUrl,
                                joinedFamilyTimestamp: familyFromServer.users_info!.find(u => u.user_id === admin.id)!.joined_at
                            },
                            pendingMembers
                        };
                    })
                );

                const familiesByID = newFamilies.reduce((map, family) => map.set(family.id.toString(), family), new ObservableMap<string, IUserFamily>());
                this._userFamilies = familiesByID;
            });
        });
    }
}



// WEBPACK FOOTER //
// ./src/Stores/UserFamilyStore.ts